import { useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Items from "../Aside/Components/Items";
import Minimize from "./Components/Minimize";
import { determineActiveIndex, shouldUseMiniAside } from "./asideState";
import { useTicketCount } from "../../Context/TicketCountContext";

const ASIDE_SCROLL_STORAGE_KEY = "asideScrollTop";

export default function Container({
  activeIndex: activeIndexProp,
  alwaysMini: alwaysMiniProp,
} = {}) {
  const { pathname } = useLocation();
  const scrollContainerRef = useRef(null);
  const { ticketCount, haveNotifications } = useTicketCount();
  const activeIndex =
    typeof activeIndexProp === "number"
      ? activeIndexProp
      : determineActiveIndex(pathname);
  const alwaysMini =
    typeof alwaysMiniProp === "boolean"
      ? alwaysMiniProp
      : shouldUseMiniAside(pathname);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem("asideCollapsed");
    return stored === "true"; // default is expanded when nothing stored
  });

  const effectiveCollapsed = alwaysMini ? true : isCollapsed;

  const handleToggle = () => {
    if (alwaysMini) return; // disable toggle
    setIsCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== "undefined") {
        localStorage.setItem("asideCollapsed", String(next));
      }
      return next;
    });
  };

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const savedScrollTop = sessionStorage.getItem(ASIDE_SCROLL_STORAGE_KEY);
    const nextScrollTop = Number(savedScrollTop);

    if (
      scrollContainerRef.current &&
      savedScrollTop !== null &&
      Number.isFinite(nextScrollTop)
    ) {
      scrollContainerRef.current.scrollTop = nextScrollTop;
    }
  }, []);

  const handleScroll = () => {
    if (typeof window === "undefined" || !scrollContainerRef.current) return;
    sessionStorage.setItem(
      ASIDE_SCROLL_STORAGE_KEY,
      String(scrollContainerRef.current.scrollTop),
    );
  };

  return (
    <aside
      className={`hidden p-3 md:flex flex-col gap-6 rounded-2xl drop-shadow-md border border-zi-blue-light bg-zi-bg2-light dark:bg-zi-bg2-dark ${
        effectiveCollapsed ? "w-[62px]" : "w-[200px]"
      }`}
    >
      <Minimize
        onToggle={handleToggle}
        isCollapsed={effectiveCollapsed}
        alwaysMini={alwaysMini}
      />
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="overflow-y-auto flex flex-col gap-6 p-0"
      >
        <div className="flex flex-col gap-3">
          <Items
            svg_name="dashboard"
            title="میز کار"
            isActive={activeIndex === 1}
            href="/dashboard"
            showTitle={!effectiveCollapsed}
          />
        </div>

        <div className="flex flex-col gap-3">
          <p
            className={`text-xs font-normal text-nowrap text-zi-blue-light/50 dark:text-white/50 ${
              effectiveCollapsed && "opacity-0"
            }`}
          >
            {!effectiveCollapsed ? "بخش مالی" : "."}
          </p>

          <Items
            svg_name="challenge"
            title="مدیریت حساب‌ها"
            isActive={activeIndex === 2}
            href="/accounts"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="transaction"
            title="مدیریت معاملات"
            isActive={activeIndex === 17}
            href="/trades"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="transaction"
            title="مدیریت تراکنش‌ها"
            isActive={activeIndex === 3}
            href="/transactions"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="sale"
            svg_classname={
              activeIndex === 18
                ? "fill-white"
                : "fill-zi-blue-dark dark:fill-white"
            }
            title="همکاری در فروش"
            isActive={activeIndex === 18}
            href="/sales-participation"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="challengeManage"
            title="مدیریت ‌چالش‌ها"
            isActive={activeIndex === 4}
            href="/challenges"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="dollar"
            title="مدیریت قیمت دلار"
            isActive={activeIndex === 19}
            href="/dollar"
            showTitle={!effectiveCollapsed}
          />
        </div>

        <div className="flex flex-col gap-3">
          <p
            className={`text-xs font-normal text-nowrap text-zi-blue-light/50 dark:text-white/50 ${
              effectiveCollapsed && "opacity-0"
            }`}
          >
            {!effectiveCollapsed ? "بخش مشتریان" : "."}
          </p>

          <Items
            svg_name="userManage"
            title="مدیریت مشتریان"
            isActive={activeIndex === 5}
            href="/customers"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="userKYC"
            title="احراز هویت مشتریان"
            isActive={activeIndex === 6}
            href="/kyc"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="userSupport"
            title="پشتیبانی مشتریان"
            isActive={activeIndex === 7}
            href="/support"
            showTitle={!effectiveCollapsed}
            haveNotifications={haveNotifications}
            notificationCount={ticketCount}
          />
        </div>

        <div className="flex flex-col gap-3">
          <p
            className={`text-xs font-normal text-nowrap text-zi-blue-light/50 dark:text-white/50 ${
              effectiveCollapsed && "opacity-0"
            }`}
          >
            {!effectiveCollapsed ? "بخش هدایا" : "."}
          </p>

          <Items
            svg_name="discount"
            title="مدیریت کد تخفیف"
            isActive={activeIndex === 8}
            href="/coupons"
            showTitle={!effectiveCollapsed}
          />
          {/* <Items
            svg_name="point"
            title="مدیریت امتیازات"
            isActive={activeIndex === 9}
            href="/Dashboard"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="competition"
            title="مدیریت مسابقات"
            isActive={activeIndex === 10}
            href="/Dashboard"
            showTitle={!effectiveCollapsed}
          /> */}
        </div>

        <div className="flex flex-col gap-3">
          <p
            className={`text-xs font-normal text-nowrap text-zi-blue-light/50 dark:text-white/50 ${
              effectiveCollapsed && "opacity-0"
            }`}
          >
            {!effectiveCollapsed ? "بخش محتوا" : "."}
          </p>

          <Items
            svg_name="notification"
            title="مدیریت اعلانات"
            isActive={activeIndex === 11}
            href="/announcements"
            showTitle={!effectiveCollapsed}
          />
          {/* <Items
            svg_name="rules"
            title="مدیریت قوانین"
            isActive={activeIndex === 12}
            href="/rules"
            showTitle={!effectiveCollapsed}
          /> */}
          {/* <Items
            svg_name="blog"
            title="مدیریت بلاگ"
            isActive={activeIndex === 13}
            href="/blogs"
            showTitle={!effectiveCollapsed}
          /> */}
        </div>

        <div className="flex flex-col gap-3">
          <p
            className={`text-xs font-normal text-nowrap text-zi-blue-light/50 dark:text-white/50 ${
              effectiveCollapsed && "opacity-0"
            }`}
          >
            {!effectiveCollapsed ? "بخش مدیریتی" : "."}
          </p>
          <Items
            svg_name="profile"
            title="حساب کاربری"
            isActive={activeIndex === 14}
            href="/profile"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="managers"
            title="مدیران سامانه"
            isActive={activeIndex === 15}
            href="/admins"
            showTitle={!effectiveCollapsed}
          />
          {/* <Items
            svg_name="log"
            title="لاگ سامانه"
            isActive={activeIndex === 16}
            href="/logs"
            showTitle={!effectiveCollapsed}
          /> */}
        </div>
      </div>
    </aside>
  );
}
